import { useState, useEffect, useMemo, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "@/lib/toast";
import {
  CalendarDays,
  Plus,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  Link as LinkIcon,
  Users,
  Bell,
  BellOff,
  Trash2,
  Edit,
  MoreVertical,
  Video,
  Calendar as CalendarIcon,
  Loader2,
  X,
  Check,
  UserPlus
} from "lucide-react";

const DAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const REMINDER_OPTIONS = [
  { value: "5m", label: "5 minutos antes" },
  { value: "15m", label: "15 minutos antes" },
  { value: "30m", label: "30 minutos antes" },
  { value: "1h", label: "1 hora antes" },
  { value: "24h", label: "24 horas antes" },
  { value: "1w", label: "1 semana antes" },
];

const EVENT_TYPES = [
  { value: "general", label: "General", color: "bg-blue-500" },
  { value: "meeting", label: "Reunión", color: "bg-purple-500" },
  { value: "appointment", label: "Cita", color: "bg-green-500" },
  { value: "reminder", label: "Recordatorio", color: "bg-amber-500" },
];

const RemindersPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: "",
    description: "",
    event_type: "general",
    start_date: "",
    start_time: "",
    end_date: "",
    end_time: "",
    location: "",
    is_online: false,
    meeting_link: "",
    reminder_times: ["1h"],
    notify_me: true,
    notify_invitees: false,
    invitees: []
  });

  const [newInvitee, setNewInvitee] = useState({ name: "", phone: "", notify: true });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const res = await api.get(`/events?month=${month}&year=${year}`);
      setEvents(res.data.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  // Fetch events
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Calendar calculations
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= totalDays; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    
    return days;
  }, [currentDate]);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      const eventDate = new Date(event.start_datetime).toISOString().split('T')[0];
      return eventDate === dateStr;
    });
  };

  // Navigation
  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Open dialog for new event
  const openNewEventDialog = (date = null) => {
    const targetDate = date || new Date();
    const dateStr = targetDate.toISOString().split('T')[0];
    
    setForm({
      title: "",
      description: "",
      event_type: "general",
      start_date: dateStr,
      start_time: "09:00",
      end_date: dateStr,
      end_time: "10:00",
      location: "",
      is_online: false,
      meeting_link: "",
      reminder_times: ["1h"],
      notify_me: true,
      notify_invitees: false,
      invitees: []
    });
    setEditingEvent(null);
    setDialogOpen(true);
  };

  // Open dialog for editing event
  const openEditDialog = (event) => {
    const startDt = new Date(event.start_datetime);
    const endDt = event.end_datetime ? new Date(event.end_datetime) : startDt;
    
    setForm({
      title: event.title,
      description: event.description || "",
      event_type: event.event_type || "general",
      start_date: startDt.toISOString().split('T')[0],
      start_time: startDt.toTimeString().slice(0, 5),
      end_date: endDt.toISOString().split('T')[0],
      end_time: endDt.toTimeString().slice(0, 5),
      location: event.location || "",
      is_online: event.is_online || false,
      meeting_link: event.meeting_link || "",
      reminder_times: event.reminder_times || ["1h"],
      notify_me: event.notify_me !== false,
      notify_invitees: event.notify_invitees || false,
      invitees: event.invitees || []
    });
    setEditingEvent(event);
    setDialogOpen(true);
  };

  // Add invitee
  const addInvitee = () => {
    if (!newInvitee.name.trim()) {
      toast.error("Ingresa el nombre del invitado");
      return;
    }
    setForm(prev => ({
      ...prev,
      invitees: [...prev.invitees, { ...newInvitee }]
    }));
    setNewInvitee({ name: "", phone: "", notify: true });
  };

  // Remove invitee
  const removeInvitee = (index) => {
    setForm(prev => ({
      ...prev,
      invitees: prev.invitees.filter((_, i) => i !== index)
    }));
  };

  // Toggle reminder time
  const toggleReminderTime = (time) => {
    setForm(prev => {
      const times = prev.reminder_times.includes(time)
        ? prev.reminder_times.filter(t => t !== time)
        : [...prev.reminder_times, time];
      return { ...prev, reminder_times: times };
    });
  };

  // Save event
  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("El título es requerido");
      return;
    }
    if (!form.start_date || !form.start_time) {
      toast.error("La fecha y hora son requeridas");
      return;
    }

    setSubmitting(true);
    try {
      const startDatetime = new Date(`${form.start_date}T${form.start_time}:00`).toISOString();
      const endDatetime = form.end_date && form.end_time 
        ? new Date(`${form.end_date}T${form.end_time}:00`).toISOString()
        : null;

      const payload = {
        title: form.title,
        description: form.description || null,
        event_type: form.event_type,
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        location: form.location || null,
        is_online: form.is_online,
        meeting_link: form.meeting_link || null,
        reminder_times: form.reminder_times,
        notify_me: form.notify_me,
        notify_invitees: form.notify_invitees,
        invitees: form.invitees
      };

      if (editingEvent) {
        await api.put(`/events/${editingEvent.id}`, payload);
        toast.success("Evento actualizado");
      } else {
        await api.post("/events", payload);
        toast.success("Evento creado");
      }

      setDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar evento");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete event
  const handleDelete = async (eventId) => {
    if (!confirm("¿Eliminar este evento?")) return;
    
    try {
      await api.delete(`/events/${eventId}`);
      toast.success("Evento eliminado");
      fetchEvents();
    } catch (error) {
      toast.error("Error al eliminar evento");
    }
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Get event type config
  const getEventTypeConfig = (type) => {
    return EVENT_TYPES.find(t => t.value === type) || EVENT_TYPES[0];
  };

  // Format time
  const formatTime = (datetime) => {
    const dt = new Date(datetime);
    return dt.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  // Selected date events
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="space-y-6" data-testid="reminders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900 dark:text-white">
            Recordatorios
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            Gestiona tus eventos y recordatorios
          </p>
        </div>
        <Button 
          onClick={() => openNewEventDialog()} 
          className="bg-cyan-700 hover:bg-cyan-800 text-white"
          data-testid="new-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Evento
        </Button>
      </div>

      {/* Calendar Container */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={goToToday}
                className="text-xs"
              >
                Hoy
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={goToPrevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={goToNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Days of Week */}
          <div className="grid grid-cols-7 mb-2">
            {DAYS.map(day => (
              <div 
                key={day} 
                className="text-center text-sm font-medium text-slate-500 dark:text-slate-400 py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#04f198]" />
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const dayEvents = getEventsForDate(day.date);
                const isSelected = selectedDate?.toDateString() === day.date.toDateString();
                
                return (
                  <div
                    key={idx}
                    onClick={() => setSelectedDate(day.date)}
                    className={`
                      min-h-[80px] p-1 border rounded-lg cursor-pointer transition-all
                      ${day.isCurrentMonth 
                        ? 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800' 
                        : 'bg-slate-50 dark:bg-slate-950 border-transparent opacity-50'}
                      ${isToday(day.date) ? 'ring-2 ring-[#04f198]' : ''}
                      ${isSelected ? 'bg-[#04f198]/10 border-[#04f198]' : 'hover:border-slate-300 dark:hover:border-slate-600'}
                    `}
                  >
                    <div className={`
                      text-sm font-medium mb-1 text-center
                      ${isToday(day.date) ? 'text-[#04f198]' : 'text-slate-700 dark:text-slate-300'}
                    `}>
                      {day.date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {dayEvents.slice(0, 2).map((event, i) => {
                        const typeConfig = getEventTypeConfig(event.event_type);
                        return (
                          <div 
                            key={i}
                            className={`${typeConfig.color} text-white text-[10px] px-1 py-0.5 rounded truncate`}
                          >
                            {event.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 2 && (
                        <div className="text-[10px] text-slate-500 text-center">
                          +{dayEvents.length - 2} más
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selected Date Events */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900 dark:text-white">
              {selectedDate 
                ? selectedDate.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })
                : 'Selecciona una fecha'}
            </h3>
            {selectedDate && (
              <Button 
                size="sm" 
                variant="ghost"
                onClick={() => openNewEventDialog(selectedDate)}
              >
                <Plus className="w-4 h-4" />
              </Button>
            )}
          </div>

          {selectedDate ? (
            selectedDateEvents.length > 0 ? (
              <div className="space-y-3">
                {selectedDateEvents.map(event => {
                  const typeConfig = getEventTypeConfig(event.event_type);
                  return (
                    <div 
                      key={event.id}
                      className="p-3 rounded-xl border border-slate-100 dark:border-slate-800 hover:border-[#04f198]/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-full min-h-[40px] ${typeConfig.color} rounded-full`} />
                          <div>
                            <h4 className="font-medium text-slate-900 dark:text-white">
                              {event.title}
                            </h4>
                            <p className="text-sm text-slate-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(event.start_datetime)}
                            </p>
                            {event.location && (
                              <p className="text-sm text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {event.location}
                              </p>
                            )}
                            {event.is_online && event.meeting_link && (
                              <p className="text-sm text-blue-500 flex items-center gap-1">
                                <Video className="w-3 h-3" />
                                Reunión online
                              </p>
                            )}
                            {/* Invitees with confirmation status */}
                            {event.invitees && event.invitees.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <p className="text-xs text-slate-400 mb-1 flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  Invitados:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {event.invitees.map((inv, idx) => (
                                    <span 
                                      key={idx}
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        inv.confirmed === true 
                                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                          : inv.confirmed === false 
                                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                      }`}
                                    >
                                      {inv.confirmed === true && <Check className="w-3 h-3 inline mr-1" />}
                                      {inv.confirmed === false && <X className="w-3 h-3 inline mr-1" />}
                                      {inv.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(event)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDelete(event.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Sin eventos</p>
                <Button 
                  variant="link" 
                  className="text-cyan-700"
                  onClick={() => openNewEventDialog(selectedDate)}
                >
                  Crear evento
                </Button>
              </div>
            )
          ) : (
            <div className="text-center py-8 text-slate-500">
              <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Haz clic en una fecha para ver los eventos</p>
            </div>
          )}
        </div>
      </div>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col w-[95vw] sm:w-full overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-['Outfit']">
              {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 px-1"
            style={{ overflowX: 'hidden' }}
          >
            {/* Title */}
            <div>
              <Label>Título *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Nombre del evento"
                data-testid="event-title-input"
              />
            </div>

            {/* Type */}
            <div>
              <Label>Tipo de evento</Label>
              <Select 
                value={form.event_type} 
                onValueChange={(v) => setForm(prev => ({ ...prev, event_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${type.color}`} />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date & Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fecha inicio *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <Label className="text-xs">Hora inicio *</Label>
                <Input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm(prev => ({ ...prev, start_time: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fecha fin</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <Label className="text-xs">Hora fin</Label>
                <Input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm(prev => ({ ...prev, end_time: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Detalles del evento"
                rows={2}
              />
            </div>

            {/* Location or Online */}
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_online}
                onCheckedChange={(v) => setForm(prev => ({ ...prev, is_online: v }))}
              />
              <Label>Reunión online</Label>
            </div>

            {form.is_online ? (
              <div>
                <Label>Link de la reunión</Label>
                <Input
                  value={form.meeting_link}
                  onChange={(e) => setForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                  placeholder="https://meet.google.com/..."
                />
              </div>
            ) : (
              <div>
                <Label>Ubicación</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Dirección o lugar"
                />
              </div>
            )}

            {/* Invitees */}
            <div>
              <Label className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Invitados
              </Label>
              <div className="mt-2 space-y-2">
                {form.invitees.map((inv, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{inv.name}</p>
                      {inv.phone && <p className="text-xs text-slate-500">{inv.phone}</p>}
                    </div>
                    {inv.notify && <Bell className="w-4 h-4 text-[#04f198]" />}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-6 w-6"
                      onClick={() => removeInvitee(idx)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                
                <div className="flex gap-2">
                  <Input
                    value={newInvitee.name}
                    onChange={(e) => setNewInvitee(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Nombre"
                    className="flex-1"
                  />
                  <Input
                    value={newInvitee.phone}
                    onChange={(e) => setNewInvitee(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+57..."
                    className="w-32"
                  />
                  <Button variant="outline" size="icon" onClick={addInvitee}>
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={newInvitee.notify}
                    onCheckedChange={(v) => setNewInvitee(prev => ({ ...prev, notify: v }))}
                    id="notify-invitee"
                  />
                  <Label htmlFor="notify-invitee" className="text-xs text-slate-500">
                    Notificar por WhatsApp
                  </Label>
                </div>
              </div>
            </div>

            {/* Reminder Times */}
            <div>
              <Label className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Recordatorios
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {REMINDER_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggleReminderTime(opt.value)}
                    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                      form.reminder_times.includes(opt.value)
                        ? 'bg-[#04f198] text-white border-[#04f198]'
                        : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-[#04f198]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Notification toggles */}
            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-slate-500" />
                  <Label>Notificarme a mí</Label>
                </div>
                <Switch
                  checked={form.notify_me}
                  onCheckedChange={(v) => setForm(prev => ({ ...prev, notify_me: v }))}
                />
              </div>
              {form.invitees.length > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-500" />
                    <Label>Notificar a invitados</Label>
                  </div>
                  <Switch
                    checked={form.notify_invitees}
                    onCheckedChange={(v) => setForm(prev => ({ ...prev, notify_invitees: v }))}
                  />
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4 flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={submitting}
              className="bg-cyan-700 hover:bg-cyan-800"
              data-testid="save-event-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingEvent ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RemindersPage;
